import { Grid, useMediaQuery } from "@material-ui/core";
import Table from "components/Table";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Cell } from "react-table";
import { loadCovenants } from "redux/covenants/covenantsFunctions";
import { RootState } from "redux/rootReducer";
import * as CovenantsTypes from "types/Covenants";
import { dateToString } from "utility/date";
import { asEur, asPercent } from "utility/numbers";
import PrivateEquityCapitalCalls from "./Overview/components/PrivateEquityCapitalCalls";
import { getReportDateSelector } from "../redux/user/user";
import ReportDatePicker from "../components/ReportDatePicker";

const Covenants = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const matchesMediaQuery = useMediaQuery("(min-width: 960px)");

  const reportControlId = useSelector(
    (state: RootState) => state.user.reportControlId
  );

  const covenantsData = useSelector(
    (state: RootState) => state.covenants.data ?? []
  );

  const covenantsLoading = useSelector(
    (state: RootState) => state.covenants.loading
  );

  const reportDate = useSelector(getReportDateSelector);

  React.useEffect(() => {
    if (reportControlId && reportDate) {
      dispatch(loadCovenants(reportControlId, reportDate));
    }
  }, [dispatch, reportControlId, reportDate]);

  const covenantsGrouped = covenantsData.reduce(
    (result, item) => ({
      ...result,
      [item.Kategoria]: [...(result[item.Kategoria ?? ""] ?? []), item],
    }),
    // tslint:disable-next-line: no-object-literal-type-assertion
    {} as { [key: string]: CovenantsTypes.Covenants[] }
  );

  const covenantsColumns = Object.keys(covenantsGrouped).map((a) => {
    const total = covenantsGrouped[a]?.find(
      (covenant) => covenant.Alakategoria === "Yhteensä"
    );

    return [
      {
        Header: a !== "SALKUT" ? a : "",
        accessor: "Alakategoria",
        paddingRight: "10px",
        minWidth: 170,
        Footer: total?.Alakategoria ?? "",
      },
      {
        Header: t("covenant"),
        accessor: "Sitoumus",
        paddingRight: "10px",
        width: "fit-content",
        Cell: (cell: Cell<CovenantsTypes.Covenants, number>) =>
          asEur(cell.value),
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: asEur(total?.Sitoumus),
      },
      {
        Header: t("uninvited_capital"),
        accessor: "KutsumatonPaaoma",
        paddingRight: "10px",
        width: "fit-content",
        Cell: (cell: Cell<CovenantsTypes.Covenants, number>) =>
          asEur(cell.value),
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: asEur(total?.KutsumatonPaaoma),
      },
      {
        Header: t("acquisition_value"),
        accessor: "HankintaArvo",
        paddingRight: "10px",
        width: "fit-content",
        Cell: (cell: Cell<CovenantsTypes.Covenants, number>) =>
          asEur(cell.value),
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: asEur(total?.HankintaArvo),
      },
      {
        Header: t("market_value"),
        accessor: "MarkkinaArvo",
        paddingRight: "10px",
        width: "fit-content",
        Cell: (cell: Cell<CovenantsTypes.Covenants, number>) =>
          asEur(cell.value),
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: asEur(total?.MarkkinaArvo),
      },
      {
        Header: t("weight"),
        accessor: "Paino",
        paddingRight: "10px",
        width: "fit-content",
        Cell: (cell: Cell<CovenantsTypes.Covenants, number>) =>
          asPercent(cell.value),
        textAlign: "right",
        fontFamily: "Lato light",
        minWidth: 70,
        Footer: asPercent(total?.Paino),
      },
      {
        Header: t("interest_and_profit"),
        accessor: "KorotJaTuotto",
        paddingRight: "10px",
        width: "fit-content",
        Cell: (cell: Cell<CovenantsTypes.Covenants, number>) =>
          asEur(cell.value),
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: asEur(total?.KorotJaTuotto),
      },
      {
        Header: t("profit_distributions"),
        accessor: "Voitonjaot",
        paddingRight: "10px",
        width: "fit-content",
        Cell: (cell: Cell<CovenantsTypes.Covenants, number>) =>
          asEur(cell.value),
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: asEur(total?.Voitonjaot),
      },
      {
        Header: t("total_profit"),
        accessor: "KokonaisTuotto",
        paddingRight: "10px",
        width: "fit-content",
        Cell: (cell: Cell<CovenantsTypes.Covenants, number>) =>
          asEur(cell.value),
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: asEur(total?.KokonaisTuotto),
      },
      {
        Header: t("capital_returns"),
        accessor: "PaaomanPalautukset",
        paddingRight: "10px",
        width: "fit-content",
        Cell: (cell: Cell<CovenantsTypes.Covenants, number>) =>
          asEur(cell.value),
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: asEur(total?.PaaomanPalautukset),
      },
      {
        Header: t("recallable"),
        accessor: "UudelleenKutsuttavaa",
        paddingRight: "10px",
        width: "fit-content",
        Cell: (cell: Cell<CovenantsTypes.Covenants, number>) =>
          asEur(cell.value),
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: asEur(total?.UudelleenKutsuttavaa),
      },
    ];
  });

  return (
    <Grid container justifyContent="center" style={{ paddingBottom: "1em" }}>
      <Grid item container md={10} sm={12} justifyContent="space-between">
        {matchesMediaQuery ? (
          <Grid item xs={7} style={{ color: "#003366" }}>
            <h1>{t("covenants")}</h1>
          </Grid>
        ) : (
          <></>
        )}
        <Grid
          container
          item
          md={2}
          sm={12}
          alignContent="center"
          justifyContent={matchesMediaQuery ? "center" : "flex-start"}
        >
          <ReportDatePicker />
        </Grid>
      </Grid>
      <Grid item container md={10} sm={12}>
        {Object.keys(covenantsGrouped).map((a, i) => (
          <Grid
            key={a}
            item
            xs={12}
            style={{
              marginBottom: 16,
              overflowY: "hidden",
            }}
          >
            <Table
              data={covenantsGrouped[a]
                .filter((c) => c.Alakategoria !== "Yhteensä")
                .map((c) => ({
                  ...c,
                  DateRange: `${dateToString(c.Alkupvm)} - ${c.Loppupvm}`,
                }))}
              columns={covenantsColumns[i]}
              loading={covenantsLoading}
              hasFooter
            />
          </Grid>
        ))}
        <PrivateEquityCapitalCalls />
      </Grid>
    </Grid>
  );
};

export default React.memo(Covenants);
